import React, { useState, useEffect } from "react";
import {
  FaClock,
  FaCalendarAlt,
  FaSearch,
  FaTimesCircle,
  FaUserAlt,
  FaFileExport,
  FaFilter,
} from "react-icons/fa";
import { getAllDocsSnap } from "../../../utils/firebase/Firebase.utils";

function EmployeeClockIns() {
  const [clockRecords, setClockRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: getLastMonthDate(),
    endDate: getCurrentDate(),
  });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Get current date in YYYY-MM-DD format
  function getCurrentDate() {
    const date = new Date();
    return date.toISOString().split("T")[0];
  }

  // Get date from last month in YYYY-MM-DD format
  function getLastMonthDate() {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  }

  useEffect(() => {
    // Fetch all clock records
    const fetchClockRecords = async () => {
      await getAllDocsSnap("clockRecords", (docs) => {
        // Sort by date and time in descending order
        docs.sort((a, b) => {
          const dateTimeA = new Date(
            `${a.date} ${a.clockInTime || a.clockOutTime}`
          );
          const dateTimeB = new Date(
            `${b.date} ${b.clockInTime || b.clockOutTime}`
          );
          return dateTimeB - dateTimeA;
        });

        // Create a set of unique employees
        const uniqueEmployees = [...new Set(docs.map((doc) => doc.userEmail))];
        setEmployees(uniqueEmployees);

        setClockRecords(docs);
        setFilteredRecords(docs);
        setIsLoading(false);
      });
    };

    fetchClockRecords();
  }, []);

  // Apply search and filters to records
  useEffect(() => {
    let filtered = [...clockRecords];

    // Apply date range filter
    filtered = filtered.filter((record) => {
      const recordDate = new Date(record.date);
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);

      // Set hours to make sure we're comparing just the dates
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return recordDate >= startDate && recordDate <= endDate;
    });

    // Apply employee filter
    if (selectedEmployee !== "all") {
      filtered = filtered.filter(
        (record) => record.userEmail === selectedEmployee
      );
    }

    // Apply search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((record) => {
        // Search in name
        if (record.userName?.toLowerCase().includes(term)) return true;
        // Search in email
        if (record.userEmail?.toLowerCase().includes(term)) return true;
        // Search in location
        if (record.location?.toLowerCase().includes(term)) return true;
        // Search in date
        if (record.date?.toLowerCase().includes(term)) return true;
        // Search in notes
        if (record.notes?.toLowerCase().includes(term)) return true;

        return false;
      });
    }

    setFilteredRecords(filtered);
  }, [searchTerm, clockRecords, dateRange, selectedEmployee]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const options = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const calculateDuration = (clockInTime, clockOutTime) => {
    if (!clockInTime || !clockOutTime) return "In Progress";

    // Parse time values considering AM/PM format
    let inHours, inMinutes, outHours, outMinutes;

    // Check if time is in 12-hour format with AM/PM
    if (
      clockInTime.includes("AM") ||
      clockInTime.includes("PM") ||
      clockInTime.includes("am") ||
      clockInTime.includes("pm")
    ) {
      // Extract hours and minutes from times like "1:30 PM" or "01:30 PM"
      const inTimeParts = clockInTime.match(/(\d+):(\d+)\s*([AaPp][Mm])/);
      const outTimeParts = clockOutTime.match(/(\d+):(\d+)\s*([AaPp][Mm])/);

      if (!inTimeParts || !outTimeParts) return "Error calculating";

      // Convert to 24-hour format
      inHours = parseInt(inTimeParts[1]);
      inMinutes = parseInt(inTimeParts[2]);
      const inPeriod = inTimeParts[3].toUpperCase();

      outHours = parseInt(outTimeParts[1]);
      outMinutes = parseInt(outTimeParts[2]);
      const outPeriod = outTimeParts[3].toUpperCase();

      // Adjust hours for PM
      if (inPeriod === "PM" && inHours < 12) inHours += 12;
      if (inPeriod === "AM" && inHours === 12) inHours = 0;

      if (outPeriod === "PM" && outHours < 12) outHours += 12;
      if (outPeriod === "AM" && outHours === 12) outHours = 0;
    } else {
      // Handle 24-hour format
      const inParts = clockInTime.split(":").map(Number);
      const outParts = clockOutTime.split(":").map(Number);

      if (inParts.length < 2 || outParts.length < 2) return "Error calculating";

      inHours = inParts[0];
      inMinutes = inParts[1];
      outHours = outParts[0];
      outMinutes = outParts[1];
    }

    // Calculate the duration in minutes
    let durationMinutes =
      outHours * 60 + outMinutes - (inHours * 60 + inMinutes);

    // Handle overnight shifts
    if (durationMinutes < 0) durationMinutes += 24 * 60;

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const exportToCSV = () => {
    if (filteredRecords.length === 0) return;

    const headers = [
      "Employee",
      "Email",
      "Date",
      "Clock In",
      "Clock Out",
      "Duration",
      "Status",
      "Location",
      "Notes",
    ];

    // Format data rows
    const rows = filteredRecords.map((record) => [
      record.userName || "N/A",
      record.userEmail || "N/A",
      record.date || "N/A",
      record.clockInTime || "N/A",
      record.clockOutTime || "N/A",
      calculateDuration(record.clockInTime, record.clockOutTime),
      record.status || "N/A",
      record.location || "N/A",
      record.notes || "",
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Create blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `employee-clock-records-${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
            <p className="mt-2 text-sm text-[#363062]">
              Loading clock records...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 bg-[#363062]/5 p-4 rounded-lg">
        <div className="flex items-center space-x-3 mb-4 md:mb-0">
          <div className="p-2 bg-[#363062] rounded-lg">
            <FaClock className="text-white text-base" />
          </div>
          <div>
            <h1 className="text-sm font-medium text-[#363062]">
              Employee Time Records
            </h1>
            <p className="text-xs text-gray-500">
              View and manage all employee clock in/out records
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <FaFilter className="w-3 h-3 mr-1.5" />
            Filters
          </button>
          <button
            onClick={exportToCSV}
            className="flex items-center px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
          >
            <FaFileExport className="w-3 h-3 mr-1.5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <h2 className="text-sm font-medium text-[#363062] mb-3">
            Filter Records
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                    <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    className="pl-7 w-full p-2 text-xs border rounded-md"
                    value={dateRange.startDate}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, startDate: e.target.value })
                    }
                  />
                </div>
                <span className="text-gray-500 text-xs">to</span>
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                    <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    className="pl-7 w-full p-2 text-xs border rounded-md"
                    value={dateRange.endDate}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, endDate: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Employee
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                  <FaUserAlt className="w-3 h-3 text-gray-400" />
                </div>
                <select
                  className="pl-7 w-full p-2 text-xs border rounded-md bg-white"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                >
                  <option value="all">All Employees</option>
                  {employees.map((email, index) => (
                    <option key={index} value={email}>
                      {email}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                  <FaSearch className="w-3 h-3 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-7 w-full p-2 text-xs border rounded-md"
                  placeholder="Search name, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="absolute inset-y-0 right-0 flex items-center pr-2"
                    onClick={() => setSearchTerm("")}
                  >
                    <FaTimesCircle className="w-3 h-3 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="mb-4 text-xs text-gray-500">
        Showing {filteredRecords.length} of {clockRecords.length} records
      </div>

      {/* Clock Records Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
        {filteredRecords.length === 0 ? (
          <div className="text-center py-12">
            <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <FaClock className="text-[#363062] text-lg" />
            </div>
            <h3 className="text-sm font-medium text-[#363062] mb-1">
              No Clock Records Found
            </h3>
            <p className="text-xs text-gray-500">
              Try adjusting your filters or search terms
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clock In
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clock Out
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.map((record, index) => (
                  <tr key={record.id || index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-6 w-6 bg-[#363062] text-white rounded-full flex items-center justify-center text-xs">
                          {record.userName
                            ? record.userName[0].toUpperCase()
                            : "U"}
                        </div>
                        <div className="ml-2">
                          <div className="text-xs font-medium text-gray-900">
                            {record.userName || "Unknown"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {record.userEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {formatDate(record.date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-green-600 font-medium">
                      {record.clockInTime || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-red-600 font-medium">
                      {record.clockOutTime || "Pending"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {calculateDuration(
                        record.clockInTime,
                        record.clockOutTime
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {record.location || "Main Office"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${
                          record.clockOutTime
                            ? "bg-gray-100 text-gray-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {record.clockOutTime ? "Completed" : "Active"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmployeeClockIns;
